import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useChartsStore } from "../../../store/chartStore";
import { useCompareStore } from "../../../store/compare";
import { shiftXExtentsByBars } from "../utils/chartHorizontalState.js";
import { shouldBumpResetKey } from "./resetKeyDecision.js";

// Stable empty array/object constants to prevent infinite re-renders
const EMPTY_INDICATORS = [];
const EMPTY_BAR_COLOR_CONFIG = [];
const EMPTY_COMPARISONS = [];

export const useChartManager = (chartId) => {
  // Track when reset is triggered to force chart refresh
  const [resetKey, setResetKey] = useState(0);

  // Track if a reset was just triggered to force proper update
  const [resetTriggered, setResetTriggered] = useState(false);

  // Store selectors
  const periodicity = useChartsStore(
    (state) => state.getChart(chartId)?.periodicity ?? undefined,
  );
  const chartType = useChartsStore(
    (state) => state.getChart(chartId)?.chartType ?? undefined,
  );
  const chartSymbol = useChartsStore(
    (state) => state.getChart(chartId)?.symbol ?? undefined,
  );
  const indicators = useChartsStore(
    (state) => state.getChart(chartId)?.indicators ?? EMPTY_INDICATORS,
  );
  const barColorProvider = useChartsStore(
    (state) => state.getChart(chartId)?.barColorProvider ?? "traditional",
  );
  const barColorConfig = useChartsStore(
    (state) =>
      state.getChart(chartId)?.barColorConfig ?? EMPTY_BAR_COLOR_CONFIG,
  );
  const renkoConfig = useChartsStore(
    (state) => state.getChart(chartId)?.renkoConfig,
  );
  const pointFigureConfig = useChartsStore(
    (state) => state.getChart(chartId)?.pointFigureConfig,
  );

  // Compare mode - access comparisons state directly (not via methods that create new objects)
  const comparisons = useCompareStore((state) => state.comparisons);
  const comparisonSymbols = comparisons[chartId]?.symbols || EMPTY_COMPARISONS;
  const isCompareMode = comparisonSymbols.length > 0;
  // Subscribe to dataVersion to trigger re-renders on real-time comparison updates
  const compareDataVersion = useCompareStore((state) => state.dataVersion);

  // Get action functions (these are stable references)
  const addComparisonSymbol = useCompareStore(
    (state) => state.addComparisonSymbol,
  );
  const removeComparisonSymbol = useCompareStore(
    (state) => state.removeComparisonSymbol,
  );
  const clearComparisons = useCompareStore((state) => state.clearComparisons);
  const reloadComparisonData = useCompareStore(
    (state) => state.reloadComparisonData,
  );

  // Memoize comparison symbols to prevent unnecessary re-renders
  const stableComparisonSymbols = useMemo(() => {
    return comparisonSymbols.length > 0 ? comparisonSymbols : EMPTY_COMPARISONS;
  }, [comparisonSymbols]);

  // Hash for forcing re-mount when Renko config changes
  // This solves the bug where ChartCanvas doesn't re-render on config change.
  // Chave completa (#82): antes só brickSize entrava — trocar engine/mode/ATR em
  // modo auto (brickSize null) não remontava e caía no caminho de redraw com
  // yScale stale (#351).
  const renkoConfigHash = useMemo(() => {
    if (chartType !== "renko" || !renkoConfig) return "";
    return `renko-${renkoConfig.brickSize ?? "auto"}-${renkoConfig.mode ?? "auto"}-${renkoConfig.atrPeriod ?? 14}-${renkoConfig.atrMultiplier ?? 1}-${renkoConfig.engine ?? "chartiq"}`;
  }, [chartType, renkoConfig]);

  // Hash for forcing re-mount when Point & Figure config changes
  const pointFigureConfigHash = useMemo(() => {
    if (chartType !== "pointfigure" || !pointFigureConfig) return "";
    return `pf-${pointFigureConfig.boxSize ?? "auto"}-${pointFigureConfig.reversal ?? 3}`;
  }, [chartType, pointFigureConfig]);

  // Data is now stored in chartData map (separate from charts metadata)
  // This prevents Workspace re-renders on every quote update
  const initialData = useChartsStore((state) => state.getData(chartId));
  const dataVersion = useChartsStore((state) => state.getDataVersion(chartId));
  const pendingInvalidation = useChartsStore((state) =>
    state.getPendingInvalidation(chartId),
  );
  const visibleBars = useChartsStore((state) => {
    const chart = state.getChart(chartId);
    return chart?.visibleBars ?? 100;
  });
  const storedXExtents = useChartsStore((state) => {
    const chart = state.getChart(chartId);
    return chart?.xExtents ?? null;
  });
  const sectionOrder = useChartsStore((state) =>
    state.getSectionOrder(chartId),
  );

  // Issue #105: Hidden chart tracking state
  const lastVisibleBarIndex = useChartsStore(
    (state) => state.getChart(chartId)?.lastVisibleBarIndex ?? null,
  );
  const wasLastBarVisible = useChartsStore(
    (state) => state.getChart(chartId)?.wasLastBarVisible ?? null,
  );
  const clearChartHiddenState = useChartsStore(
    (state) => state.clearChartHiddenState,
  );

  // Store actions
  const updateVisibleBars = useChartsStore((state) => state.updateVisibleBars);
  const updateXExtents = useChartsStore((state) => state.updateXExtents);
  const getSectionOrder = useChartsStore((state) => state.getSectionOrder);
  const getDefaultSectionOrder = useChartsStore(
    (state) => state.getDefaultSectionOrder,
  );
  const setSectionOrder = useChartsStore((state) => state.setSectionOrder);
  const moveSectionUp = useChartsStore((state) => state.moveSectionUp);
  const moveSectionDown = useChartsStore((state) => state.moveSectionDown);

  // Force ChartCanvas remount when symbol changes.
  // The canvas-based ChartCanvas doesn't re-render from new data props alone;
  // incrementing resetKey changes the React key, forcing a full remount.
  const prevSymbolRef = useRef(chartSymbol);
  useEffect(() => {
    if (shouldBumpResetKey(prevSymbolRef.current, chartSymbol)) {
      setResetKey((prev) => prev + 1);
    }
    prevSymbolRef.current = chartSymbol;
  }, [chartSymbol]);

  // Reset the resetTriggered flag after chart has updated
  useEffect(() => {
    if (resetTriggered) {
      setResetTriggered(false);
    }
  }, [storedXExtents, resetTriggered]);

  // Issue #105: Detect new bars on mount and auto-scroll if needed
  useEffect(() => {
    // Skip if no hidden state was stored
    if (lastVisibleBarIndex === null || wasLastBarVisible === null) return;

    // Skip if no data yet
    if (!initialData || initialData.length === 0) return;

    const currentLastBarIndex = initialData.length - 1;
    const newBarsCount = currentLastBarIndex - lastVisibleBarIndex;

    // Only auto-scroll if:
    // 1. New bars arrived while hidden (newBarsCount > 0)
    // 2. The last bar WAS visible when chart was hidden
    if (newBarsCount > 0 && wasLastBarVisible && storedXExtents) {
      updateXExtents(
        chartId,
        shiftXExtentsByBars(storedXExtents, newBarsCount),
      );
    }

    // Clear hidden state after handling (regardless of whether we scrolled)
    clearChartHiddenState(chartId);
  }, [
    chartId,
    initialData,
    lastVisibleBarIndex,
    wasLastBarVisible,
    storedXExtents,
    updateXExtents,
    clearChartHiddenState,
  ]);

  // Reload comparison data when periodicity changes
  const prevPeriodicityRef = useRef(periodicity);
  useEffect(() => {
    // Skip if no periodicity yet or if it hasn't changed
    if (!periodicity || periodicity === prevPeriodicityRef.current) {
      prevPeriodicityRef.current = periodicity;
      return;
    }

    // Skip if no comparison symbols
    if (!isCompareMode) {
      prevPeriodicityRef.current = periodicity;
      return;
    }

    prevPeriodicityRef.current = periodicity;

    // Reload comparison data with new periodicity
    reloadComparisonData(chartId, { periodicity });
  }, [chartId, periodicity, isCompareMode, reloadComparisonData]);

  // Handle chart reset
  const handleReset = useCallback(() => {
    setResetTriggered(true);
    updateVisibleBars(chartId, 100);
    updateXExtents(chartId, null);
    setResetKey((prev) => prev + 1);
  }, [chartId, updateVisibleBars, updateXExtents]);

  // Callback for ChartCanvas direct updates
  const handleChartCanvasVisibleBarsChange = useCallback(
    (newVisibleBars) => {
      if (newVisibleBars !== visibleBars && newVisibleBars > 0) {
        updateVisibleBars(chartId, newVisibleBars);
      }
    },
    [chartId, visibleBars, updateVisibleBars],
  );

  // Callback for ChartCanvas xExtents updates (after pan/zoom)
  const handleChartCanvasXExtentsChange = useCallback(
    (newXExtents) => {
      if (
        newXExtents &&
        Array.isArray(newXExtents) &&
        newXExtents.length === 2
      ) {
        updateXExtents(chartId, newXExtents);
      }
    },
    [chartId, updateXExtents],
  );

  // Callback for ChartCanvas onLoadMore (when scrolling near edge)
  // Disable for aggregation chart types (P&F, Renko, etc) - loading more data would recalculate columns
  const AGGREGATION_CHART_TYPES = [
    "pointfigure",
    "renko",
    "kagi",
    "linebreak",
    "rangebars",
  ];
  const loadMoreHistoricalBars = useChartsStore(
    (state) => state.loadMoreHistoricalBars,
  );
  const handleLoadMore = useCallback(
    (start, end) => {
      // Don't load more data for aggregation types - it would recalculate and change the chart
      if (AGGREGATION_CHART_TYPES.includes(chartType)) {
        console.log(
          `[handleLoadMore] Disabled for aggregation type: ${chartType}`,
        );
        return;
      }
      console.log(`[handleLoadMore] Triggered at start=${start}, end=${end}`);
      loadMoreHistoricalBars(chartId);
    },
    [chartId, chartType, loadMoreHistoricalBars],
  );

  return {
    // State
    resetKey,
    resetTriggered,
    setResetTriggered,

    // Chart data
    periodicity,
    chartType,
    chartSymbol,
    indicators,
    barColorProvider,
    barColorConfig,
    renkoConfig,
    renkoConfigHash,
    pointFigureConfig,
    pointFigureConfigHash,
    initialData,
    dataVersion, // Version counter for data mutations (performance optimization)
    pendingInvalidation,
    visibleBars,
    storedXExtents,
    sectionOrder,

    // Compare mode
    isCompareMode,
    comparisonSymbols: stableComparisonSymbols,
    compareDataVersion, // Version counter for real-time comparison data updates
    addComparisonSymbol,
    removeComparisonSymbol,
    clearComparisons,
    reloadComparisonData,

    // Section management
    getSectionOrder,
    getDefaultSectionOrder,
    setSectionOrder,
    moveSectionUp,
    moveSectionDown,

    // Actions
    updateVisibleBars,
    updateXExtents,
    handleReset,
    handleChartCanvasVisibleBarsChange,
    handleChartCanvasXExtentsChange,
    handleLoadMore,
  };
};
